<script>
export default {
  name: "SimilarityPill",

  props: {
    similarity: {
      type: Number,
      required: true,
    },
    // Override auto-calculated variant
    variant: {
      type: String,
      default: null,
      validator: (value) =>
        ["danger", "warning", "neutral", "success"].includes(value),
    },
    filled: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    computedVariant() {
      if (this.variant) return this.variant;

      if (this.similarity >= 50) return "danger";
      if (this.similarity >= 12) return "warning";
      if (this.similarity > 0) return "neutral";
      return "success";
    },

    pillClass() {
      return [
        "similarity-pill",
        `similarity-pill--${this.computedVariant}`,
        { "similarity-pill--filled": this.filled },
      ];
    },

    iconName() {
      if (this.computedVariant === "success") return "check";
      return "information";
    },
  },
};
</script>

<template>
  <div :class="pillClass" class="caption-s medium">
    <eg-icon v-if="showIcon" :icon="iconName" size="small" class="pill-icon" />
    <span class="pill-text">{{ similarity }}%</span>
  </div>
</template>

<style scoped lang="scss">
.similarity-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 3px 8px;
  border-radius: var(--radius-rd-500, 16px);
  border: 1px solid;
  white-space: nowrap;

  // Danger variant (≥70%)
  &--danger {
    border-color: var(--color-stroke-semantic-onerror, #dc3545);
    color: var(--color-text-semantic-onerror, #ff3b30);

    &.similarity-pill--filled {
      background: var(--color-surface-semantic-error-error, #dc3545);
      border-color: var(--color-surface-semantic-error-error, #dc3545);
      color: #fff;
    }
  }

  // Warning variant (20-49%)
  &--warning {
    border-color: var(--color-stroke-semantic-onwarning, #d76812);
    color: var(--color-text-semantic-onwarning, #d76812);

    &.similarity-pill--filled {
      background: var(--color-surface-semantic-warning-default, #d76812);
      border-color: var(--color-surface-semantic-warning-default, #d76812);
      color: #fff;
    }
  }

  // Neutral variant (<20%)
  &--neutral {
    border-color: var(--color-stroke-neutral-disabled, #d3d9de);
    color: var(--color-text-neutral-secondary, #566370);

    &.similarity-pill--filled {
      background: var(--color-surface-neutral-tertiary, #566370);
      border-color: var(--color-surface-neutral-tertiary, #566370);
      color: #fff;
    }
  }

  // Success variant (0%)
  &--success {
    border-color: var(--color-stroke-semantic-onsuccess, #28a745);
    color: var(--color-text-semantic-onsuccess, #28a745);

    &.similarity-pill--filled {
      background: var(--color-surface-semantic-success-default, #28a745);
      border-color: var(--color-surface-semantic-success-default, #28a745);
      color: #fff;
    }
  }
}

.pill-icon {
  flex-shrink: 0;
}

.pill-text {
  line-height: 1.35;
}
</style>
